import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useFetchAllRestaurantsQuery } from "../../store";
import { twMerge } from "tailwind-merge";
import { FaSearch, FaTimes } from "react-icons/fa";
import { Restaurant } from "../../types";
import useDebounce from "../../hooks/useDebounce";

interface SearchBarProps {
  value?: Restaurant | null;
  placeholder?: string;
  className?: string;
  setRestaurant: (value: Restaurant | null) => void;
}

const RestaurantSearchBar: React.FC<SearchBarProps> = ({
  value,
  placeholder,
  className,
  setRestaurant,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const divEl = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchText, 400);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!divEl.current) return;
      if (!divEl.current.contains(event.target as Node)) setIsOpen(false);
    };

    document.addEventListener("click", handler, false);
    return () => {
      document.removeEventListener("click", handler);
    };
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm.length > 1) setIsOpen(true);
    else setIsOpen(false);
  }, [debouncedSearchTerm]);

  const { data: restaurants, isFetching: isFetchingRestaurants } =
    useFetchAllRestaurantsQuery(debouncedSearchTerm, {
      skip: debouncedSearchTerm.length < 2,
    });

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleOptionClick = (option: Restaurant) => {
    setIsOpen(false);
    setSearchText("");
    setRestaurant(option);
  };

  const handleClear = () => {
    setRestaurant(null);
    setSearchText("");
  };

  const wrapperClasses = classNames(
    "relative flex flex-col justify-center shadow-lg",
    className
  );

  const inputContainerClasses = twMerge(
    classNames("w-full relative flex items-center rounded p-2")
  );

  const inputClasses = classNames("w-full text-lg focus:outline-none");

  const renderedOptions = restaurants?.map((restaurant) => (
    <div
      key={restaurant._id}
      className="hover:bg-gray-200 rounded cursor-pointer p-1"
      onClick={() => handleOptionClick(restaurant)}
    >
      {restaurant.displayName}
    </div>
  ));

  return (
    <div className={wrapperClasses}>
      <div ref={divEl} className={inputContainerClasses}>
        <FaSearch className="text-primary text-lg mr-2" />
        <input
          value={
            isFocused ? searchText : searchText || value?.displayName || ""
          }
          placeholder={placeholder}
          className={inputClasses}
          onChange={handleOnChange}
          autoComplete="off"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        {value && (
          <FaTimes
            className="text-gray-400 cursor-pointer ml-2 hover:text-gray-600"
            onClick={handleClear}
          />
        )}
      </div>

      {isOpen &&
        (isFetchingRestaurants ? (
          <div>loading...</div>
        ) : (
          <div className="absolute top-full w-full border bg-white overflow-y-auto max-h-60 p-2 z-50 shadow-md rounded-b">
            {renderedOptions && renderedOptions.length ? (
              renderedOptions
            ) : (
              <div className="flex flex-col rounded">
                <div>Aucun restaurant trouvé</div>
              </div>
            )}
          </div>
        ))}
    </div>
  );
};

export default RestaurantSearchBar;
